/* eslint-disable lines-between-class-members */
import * as THREE from "three";
import { ScriptableMixin, ScriptableObject, ScriptableObjects } from "./ScriptableObjects.js";

/* ────────────────────────────────────────────────────────────────────────── */
/* Environment & helpers                                                      */
/* ────────────────────────────────────────────────────────────────────────── */
const HAS_AFRAME = typeof window !== "undefined" && !!window.AFRAME;
// eslint-disable-next-line no-undef
const MirrorCtor = (typeof globalThis !== "undefined" && (globalThis.MirrorNode || globalThis.Mirror)) || undefined;

const isAFrameEntity = v => !!(v && typeof v.addEventListener === "function" && v.object3D);
const isObject3D = v => !!(v && (v.isObject3D || (typeof THREE !== "undefined" && v instanceof THREE.Object3D)));

/* ────────────────────────────────────────────────────────────────────────── */
/* Public API: properties                                                     */
/* ────────────────────────────────────────────────────────────────────────── */
export const MIRROR_PROPS = ["color"];

/* ────────────────────────────────────────────────────────────────────────── */
/* Editor / plain-Three adapter                                               */
/* ────────────────────────────────────────────────────────────────────────── */
class MirrorEditorAdapter {
  constructor(sourceObject, originalDescriptors = new Map()) {
    this.sourceObject = sourceObject;
    this._values = new Map();
    this._originalDescriptors = originalDescriptors;

    MIRROR_PROPS.forEach(prop => {
      Object.defineProperty(this, prop, {
        get: () => this._values.get(prop),
        set: v => {
          this._values.set(prop, v);

          // Special handling for color (THREE.Color)
          if (prop === "color") {
            const color = this.sourceObject[prop] && this.sourceObject[prop].isColor
              ? this.sourceObject[prop]
              : new THREE.Color();
            try {
              color.set(v);
              this._values.set(prop, color);

              const originalDescriptor = this._originalDescriptors.get(prop);
              if (originalDescriptor && originalDescriptor.set) {
                originalDescriptor.set.call(this.sourceObject, color);
              } else {
                console.warn(`Could not set color for ${prop}: no original setter found.`);
              }
            } catch (e) {
              console.warn(`Failed to set color for ${prop}:`, e);
            }
            return;
          }

          // Generic property setting
          const originalDescriptor = this._originalDescriptors.get(prop);
          if (originalDescriptor && originalDescriptor.set) {
            try {
              originalDescriptor.set.call(sourceObject, v);
            } catch (e) {
              console.warn(`Failed to call original setter for ${prop}:`, e);
            }
          } else {
            try {
              sourceObject[prop] = v;
            } catch (e) {
              // ignore
            }
          }
        }
      });

      // seed initial value (best-effort, avoid triggering setters)
      const d = originalDescriptors.get(prop);
      let initial;
      try {
        if (d?.get) initial = d.get.call(sourceObject);
        else if (Object.prototype.hasOwnProperty.call(sourceObject, prop)) initial = sourceObject[prop];
      } catch {
        /* ignore */
      }
      this._values.set(prop, initial);
    });
  }

  dispose() {
    this._values.clear();
    this.sourceObject = null;
  }
}

/* ────────────────────────────────────────────────────────────────────────── */
/* A-Frame adapter                                                            */
/* ────────────────────────────────────────────────────────────────────────── */
class MirrorAFrameAdapter {
  constructor(sourceObject) {
    this.sourceObject = sourceObject;

    const COMPONENT = "mirror";
    const pending = (this._pendingUpdates = {});

    const flush = () => {
      const el = this.sourceObject?.el;
      if (!el) return;

      if (Object.keys(pending).length > 0) {
        const current = { ...(el.getAttribute(COMPONENT) || {}) };
        Object.assign(current, pending);
        el.setAttribute(COMPONENT, current);
        Object.keys(pending).forEach(key => delete pending[key]);
      }
    };

    this.sourceObject?.el?.addEventListener("componentinitialized", e => {
      if (e?.detail?.name === COMPONENT) {
        flush();
      }
    });

    const setComponentProp = (key, val) => {
      const el = this.sourceObject?.el;
      if (!el) return;
      const attr = el.getAttribute(COMPONENT);
      if (!attr) {
        pending[key] = val;
      } else {
        const current = { ...attr };
        current[key] = val;
        el.setAttribute(COMPONENT, current);
      }
    };

    const getComponentProp = key => {
      const el = this.sourceObject?.el;
      if (!el) return pending[key];
      const attr = el.getAttribute(COMPONENT);
      return (attr && attr[key]) ?? pending[key];
    };

    // Map each prop
    MIRROR_PROPS.forEach(prop => {
      Object.defineProperty(this, prop, {
        get: () => getComponentProp(prop),
        set: v => setComponentProp(prop, v)
      });
    });
  }

  dispose() {
    this.sourceObject = null;
  }
}

/* ────────────────────────────────────────────────────────────────────────── */
/* Lazy adapter bootstrap (works for adopted instances)                       */
/* ────────────────────────────────────────────────────────────────────────── */
function collectDescriptorsFor(obj) {
  const map = new Map();
  for (const prop of MIRROR_PROPS) {
    let cur = obj;
    while (cur) {
      const d = Object.getOwnPropertyDescriptor(cur, prop);
      if (d) {
        map.set(prop, d);
        break;
      }
      cur = Object.getPrototypeOf(cur);
    }
    if (!map.has(prop)) map.set(prop, undefined);
  }
  return map;
}

function ensureAdapter(self) {
  if (self._adapter) return self._adapter;

  if (HAS_AFRAME && (self.el || self.__el)) {
    Object.defineProperty(self, "_adapter", { value: new MirrorAFrameAdapter(self), configurable: true });
    return self._adapter;
  }

  const descriptors = collectDescriptorsFor(self);
  Object.defineProperty(self, "_adapter", { value: new MirrorEditorAdapter(self, descriptors), configurable: true });
  return self._adapter;
}

/* ────────────────────────────────────────────────────────────────────────── */
/* Main MirrorElement class                                                   */
/* ────────────────────────────────────────────────────────────────────────── */
export class MirrorElement extends ScriptableObject {
  static scriptableType = "mirror";

  constructor(initial = {}) {
    super();

    if (HAS_AFRAME) {
      const el = document.createElement("a-entity");
      // Default values mirror those in MirrorNode constructor
      el.setAttribute("mirror", { color: "#7f7f7f", ...initial });

      const obj = this.constructor.adopt(el); // identity-preserving prototype shift
      if (Object.getPrototypeOf(obj) !== this.constructor.prototype) {
        Object.setPrototypeOf(obj, this.constructor.prototype);
      }

      // lazy adapter + apply props (if any)
      ensureAdapter(obj);
      for (const k of Object.keys(initial)) {
        if (k in obj) obj[k] = initial[k];
      }
      return obj;
    }

    // editor / plain Three
    if (!this.scriptableType) {
      Object.defineProperty(this, "scriptableType", { value: this.constructor.scriptableType, configurable: true });
    }

    const node = createMirrorElementEditor(initial);
    ensureAdapter(node);

    for (const k of Object.keys(initial)) {
      if (k in node) node[k] = initial[k];
    }

    return node;
  }

  copy(source, recursive = true) {
    if (!source || !source.isScriptableObject) return this;

    // 1) copy transform only (no children)
    THREE.Object3D.prototype.copy.call(this, source, /* recursive = */ false);

    // 2) copy public mirror props via adapter (deep where needed)
    const take = (prop, val) => {
      if (val === undefined) return;
      if (prop === "color" && val?.isColor) {
        this[prop] = val.clone();
      } else if (Array.isArray(val)) {
        this[prop] = val.slice();
             } else if (val && typeof val.clone === "function") {
         this[prop] = val.clone();
       } else {
         this[prop] = val;
       }
    };

    for (const p of MIRROR_PROPS) {
      take(p, source[p]);
    }

    // 3) clear existing logical children
    if (this.children?.length) {
      for (const c of [...this.children]) THREE.Object3D.prototype.remove.call(this, c);
    }

    // 4) replicate children via our public API
    if (recursive) {
      for (const srcChild of source.children || []) {
        this.add(srcChild.clone());
      }
    }

    return this;
  }

  clone(recursive = true) {
    // Build init payload so the new element is born with correct component state
    const init = {};
    for (const p of MIRROR_PROPS) {
      const v = this[p];
      if (v === undefined) continue;

      if (p === "color" && v?.isColor) {
        init[p] = v.clone();
      } else if (Array.isArray(v)) {
        init[p] = v.slice();
      } else if (v && typeof v.clone === "function") {
        init[p] = v.clone();
      } else {
        init[p] = v;
      }
    }

    // Prefer factory if available (handles adopt/constructor nuances)
    const Ctor = this.constructor && typeof this.constructor.create === "function" ? this.constructor : MirrorElement;

    const dest = typeof Ctor.create === "function" ? Ctor.create(init) : new MirrorElement(init);

    // Write object3D transforms (component sync for A-Frame to be added later)
    dest.position.copy(this.position);
    dest.quaternion.copy(this.quaternion);
    dest.scale.copy(this.scale);

    // Copy remaining fields & children
    return dest.copy(this, recursive);
  }

  static create(initial = {}) {
    return new this(initial);
  }
}

/* ────────────────────────────────────────────────────────────────────────── */
/* DRY helper: upgrade a plain Editor MirrorNode instance to Scriptable      */
/* ────────────────────────────────────────────────────────────────────────── */
function makeEditorMirrorNodeScriptable(node) {
  if (!node) return node;
  if (node.isScriptableObject === true) return node;

  // Preserve original chain and add Scriptable API on top
  const originalProto = Object.getPrototypeOf(node);
  const ScriptableClass = ScriptableMixin((originalProto && originalProto.constructor) || MirrorCtor || THREE.Object3D);
  if (Object.getPrototypeOf(ScriptableClass.prototype) !== originalProto) {
    Object.setPrototypeOf(ScriptableClass.prototype, originalProto);
  }
  Object.setPrototypeOf(node, ScriptableClass.prototype);

  // Annotate type
  try {
    if (!node.scriptableType) Object.defineProperty(node, "scriptableType", { value: MirrorElement.scriptableType, configurable: true });
  } catch (_) {
    node.scriptableType = MirrorElement.scriptableType;
  }

  // Attach adapter and per-instance property proxies
  const descriptors = collectDescriptorsFor(node);
  try {
    Object.defineProperty(node, "_adapter", { value: new MirrorEditorAdapter(node, descriptors), configurable: true });
  } catch (_) {
    node._adapter = new MirrorEditorAdapter(node, descriptors);
  }

  for (const prop of MIRROR_PROPS) {
    try {
      Object.defineProperty(node, prop, {
        get() {
          return this._adapter?.[prop];
        },
        set(v) {
          if (this._adapter) this._adapter[prop] = v;
        },
        enumerable: true,
        configurable: true
      });
    } catch (_) {
      /* ignore define errors */
    }
  }

  return node;
}

/* ────────────────────────────────────────────────────────────────────────── */
/* Bind property surface (works for adopted instances too)                    */
/* ────────────────────────────────────────────────────────────────────────── */
MIRROR_PROPS.forEach(prop => {
  Object.defineProperty(MirrorElement.prototype, prop, {
    get() {
      return ensureAdapter(this)?.[prop];
    },
    set(v) {
      const a = ensureAdapter(this);
      if (a) a[prop] = v;
    },
    enumerable: true,
    configurable: true
  });
});

/* ────────────────────────────────────────────────────────────────────────── */
/* Optional editor factory helper                                             */
/* ────────────────────────────────────────────────────────────────────────── */
export function createMirrorElementEditor(initial = {}) {
  const MirrorCtor = globalThis.MirrorNode;

  if (typeof MirrorCtor !== "function") {
    throw new Error("MirrorElementEditor: MirrorNode constructor not found.");
  }
  let node;
  try {
    const editor = ScriptableObject.editor || globalThis.editor;
    node = new MirrorCtor(editor);
  } catch (e) {
    throw new Error(`MirrorElementEditor: failed to construct – ${e?.message || e}`);
  }

  makeEditorMirrorNodeScriptable(node);

  for (const k of Object.keys(initial)) {
    if (k in node) node[k] = initial[k];
  }
  return node;
}

/* ────────────────────────────────────────────────────────────────────────── */
/* Registry: promote A-Frame elements and pure-Three objects                  */
/* ────────────────────────────────────────────────────────────────────────── */
ScriptableObjects.registerType("mirror", source => {
  if (!source) return source;

  const type = source.scriptableType || source.object3D?.scriptableType || source?.__el?.object3D?.scriptableType || source?.el?.object3D?.scriptableType;

  if (type !== MirrorElement.scriptableType) return source;

  // A-Frame entities: use base adopt via class method
  if (isAFrameEntity(source)) return MirrorElement.adopt(source);

  // Editor/plain-Three nodes: upgrade in-place via shared helper
  if (isObject3D(source)) return makeEditorMirrorNodeScriptable(source);

  return source;
});

/* Optional: nicer DevTools label */
Object.defineProperty(MirrorElement.prototype, Symbol.toStringTag, { value: "MirrorElement" });

// Named exports expected by external consumers
export { MirrorAFrameAdapter, MirrorEditorAdapter };
